import { Injectable } from '@angular/core';
import {
   HttpInterceptor,
   HttpRequest,
   HttpHandler,
   HttpEvent
} from '@angular/common/http';
import { Observable } from 'rxjs';

import { AuthService } from '../services/auth.service';          // Ajustar ruta según tu proyecto
import { ExternalConfService } from '@core/services/external-conf.service';// Ajustar ruta según tu proyecto

@Injectable({
   providedIn: 'root'
})
export class AuthTokenInterceptor implements HttpInterceptor {

   constructor(
      private authService: AuthService,
      private externalConfService: ExternalConfService
   ) { }

   intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

      // Verificar si debemos o no inyectar el token
      if (!this.shouldIntercept(request)) {
         const cleanRequest = request.headers.has('X-Skip-Token')
            ? request.clone({
               headers: request.headers.delete('X-Skip-Token')
            })
            : request;

         return next.handle(cleanRequest);
      }

      // Obtener el token y sgiindice del AuthService
      const token = this.authService.getToken();
      const sgiindice = this.authService.getSgiindice();
      // Los endpoints globales (Indice/*, UsuariosAdmin/*, Traducciones/*,
      // Herramientas/chequeabd_sgi) NO deben llevar sgiindice. Ver FlujoLoginSGInscripciones.pdf.
      const incluirSgiindice = this.requiereSgiindice(request.url);

      let modifiedRequest = request;

      // El backend espera token y sgiindice en el BODY (form-urlencoded), NO en headers
      if (request.method.toUpperCase() === 'POST' && token) {
         modifiedRequest = this.handlePostRequest(request, token, incluirSgiindice ? sgiindice : null);
      }

      if (this.externalConfService.DEBUG_HTTP) {
         console.warn('==== Interceptor Debug: AuthTokenInterceptor ====');
         console.warn('URL:', modifiedRequest.url);
         console.warn('Method:', modifiedRequest.method);
         console.warn('Should Intercept:', this.shouldIntercept(request));
         console.warn('Token:', token ? 'Present' : 'Missing');
         console.warn('Params:', modifiedRequest.params?.toString());

         const sanitizedBody = this.sanitizeBody(modifiedRequest.body);

         if (sanitizedBody instanceof FormData) {
            console.warn('Body: [FormData]');
            sanitizedBody.forEach((value, key) => {
               console.warn(`   ${key} -> `, value);
            });
         } else {
            console.warn('Body:', sanitizedBody);
         }
      }

      return next.handle(modifiedRequest);
   }

   /**
    * Sanitiza el body de la petición para evitar exponer información sensible en los logs.
    * Actualmente solo maneja FormData y objetos JSON simples. Y solo oculta el campo 'password'.
    */
   private sanitizeBody(body: any): any {
      if (body instanceof FormData) {
         const clonedFormData = new FormData();
         body.forEach((value, key) => {
            if (key === 'password') {
               clonedFormData.append(key, '****');
            } else {
               clonedFormData.append(key, value);
            }
         });
         return clonedFormData;
      } else if (typeof body === 'object' && body !== null) {
         // Para objetos JSON, clona y oculta campos sensibles
         const clonedBody = { ...body };
         if ('password' in clonedBody) {
            clonedBody.password = '****';
         }
         return clonedBody;
      }
      return body;
   }


   /**
    * Decide si interceptar la petición:
    * - Si el request contiene el header 'X-Skip-Token', se salta el token
    * - Si la URL apunta a un fichero, se salta el token
    * - Si la URL es una ruta pública (/public/*), se salta el token
    * - Si la URL es de login/auth, se salta el token
    */
   private shouldIntercept(request: HttpRequest<any>): boolean {
      // 1) Verificar si se ha añadido un header que fuerza el salto del token
      if (request.headers.has('X-Skip-Token')) {
         return false;
      }

      // 2) Verificar si es una ruta pública (sin autenticación)
      const url = request.url.toLowerCase();

      // Rutas públicas genéricas (personaliza según necesidad)
      if (url.includes('/public/')) {
         return false;
      }

      // Rutas de autenticación (no requieren token)
      if (url.includes('/auth/')) {
         return false;
      }

      // 3) Verificar si la URL apunta a un tipo de archivo que exima el uso de token
      const excludedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.zip', '.rar', '.tar', '.gz', '.7z'];
      const lowerUrl = request.url.toLowerCase();

      if (excludedExtensions.some(ext => lowerUrl.endsWith(ext))) {
         return false;
      }

      return true;
   }

   /**
    * Indica si la peticion debe llevar el parametro `sgiindice` en el body.
    *
    * Segun el documento FlujoLoginSGInscripciones, los siguientes endpoints
    * NUNCA deben llevar `sgiindice` porque operan a nivel de BD maestra o
    * son independientes del congreso:
    *   - UsuariosAdmin/*
    *   - Indice/*
    *   - Traducciones/*
    *   - Herramientas/chequeabd_sgi
    *
    * El resto SI deben llevarlo (obligatorio).
    */
   private requiereSgiindice(url: string): boolean {
      const lower = url.toLowerCase();
      const exclusiones = [
         '/indice/',
         '/usuariosadmin/',
         '/traducciones/',
         '/herramientas/chequeabd_sgi',
      ];
      return !exclusiones.some(ruta => lower.includes(ruta));
   }

   /**
    * Maneja las peticiones GET añadiendo un nuevo query param 'token'
    */
   private handleGetRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
      // Agregar param ?token=xxxx
      const updatedParams = request.params.set('token', token);
      return request.clone({
         params: updatedParams
      });
   }

   /**
    * Maneja las peticiones POST añadiendo 'token' y 'sgiindice' al body.
    * Soporta FormData y URLSearchParams (application/x-www-form-urlencoded).
    */
   private handlePostRequest(request: HttpRequest<any>, token: string, sgiindice: number | null): HttpRequest<any> {
      const sgiindiceStr = sgiindice == null ? '0' : String(sgiindice);

      // Si el body es FormData
      if (request.body instanceof FormData) {
         const formData = request.body;

         if (!formData.has('token') && token) {
            formData.append('token', token);
         }

         if (!formData.has('sgiindice') && sgiindice != null) {
            formData.append('sgiindice', String(sgiindice));
         }

         return request.clone({ body: formData });
      }

      // Si el body es string (URLSearchParams convertido a string).
      // Importante: solo se INYECTA sgiindice si el servicio no lo ha puesto ya.
      // Si lo machacamos, perdemos el valor correcto que el componente paso
      // desde la URL (idCongreso) y enviamos el de storage (que para
      // superadmin es 0 y el back rechaza con "sgiindice erroneo").
      if (typeof request.body === 'string') {
         const params = new URLSearchParams(request.body);
         if (!params.has('token') && token) {
            params.set('token', token);
         }
         if (!params.has('sgiindice') && sgiindice != null) {
            params.set('sgiindice', sgiindiceStr);
         }
         return request.clone({ body: params.toString() });
      }

      // Body nulo (POST sin cuerpo) -> crear uno con token (+sgiindice si aplica)
      if (request.body == null) {
         const params = new URLSearchParams();
         params.set('token', token);
         if (sgiindice != null) {
            params.set('sgiindice', sgiindiceStr);
         }
         return request.clone({
            body: params.toString(),
            setHeaders: { 'Content-Type': 'application/x-www-form-urlencoded' },
         });
      }

      return request;
   }
}
